import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  to_be_contacted: "To be contacted",
  contacted: "Contacted",
  invited: "Invited",
  archived: "Archived",
};
const STATUS_CLS = {
  to_be_contacted: "bg-slate-100 text-slate-600",
  contacted: "bg-blue-100 text-blue-700",
  invited: "bg-emerald-100 text-emerald-700",
  archived: "bg-amber-100 text-amber-700",
};

export const FundraiserReferralCard = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try { const res = await apiClient.get("/fundraiser-referrals/mine"); setReferrals(res.data || []); }
    catch (e) { /* non-fatal */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.firstName.trim()) { toast.error("First name is required"); return; }
    setSaving(true);
    try {
      await apiClient.post("/fundraiser-referrals", form);
      toast.success("Referral submitted — your manager will review it.");
      setForm({ firstName: "", lastName: "", phone: "", email: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit referral");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="fundraiser-referral-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-emerald-600" />
            <h3 className="font-heading font-bold text-slate-900">Refer a Fundraiser</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpen(!open)} data-testid="toggle-referral-form-btn">
            {open ? "Close" : "Refer someone"}
          </Button>
        </div>

        {open && (
          <div className="space-y-2 mb-4 p-3 bg-slate-50 rounded-lg" data-testid="referral-form">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name *" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="referral-firstname" />
              <Input placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="referral-lastname" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="referral-phone" />
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="referral-email" />
            </div>
            <Textarea placeholder="Notes / why they'd be a great fundraiser" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="referral-notes" />
            <Button onClick={submit} disabled={saving} className="w-full" data-testid="submit-referral-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit referral"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-4 text-slate-400">Loading...</div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-4 text-slate-400 text-sm" data-testid="no-referrals">You haven't referred anyone yet.</div>
        ) : (
          <div className="space-y-2" data-testid="my-referrals-list">
            {referrals.map((r) => (
              <div key={r.id} className="p-3 border border-slate-100 rounded-lg" data-testid={`my-referral-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{r.firstName} {r.lastName}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[r.status] || "bg-slate-100"}`}>{STATUS_LABEL[r.status] || r.status}</span>
                </div>
                {(r.bonusAmount != null || r.bonusConditions) && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid={`referral-bonus-${r.id}`}>
                    Bonus: {r.bonusAmount != null ? `£${Number(r.bonusAmount).toFixed(2)}` : "TBC"}{r.bonusConditions ? ` — ${r.bonusConditions}` : ""}
                  </p>
                )}
                {r.managerNotes && (
                  <p className="text-xs text-slate-500 mt-1">Note: {r.managerNotes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
